/** Where Conversations is, read from the address, and the addresses of its places. */
import type { HostRoomRow, HostTalkResponse } from "../../shared/talk";

export const talkPath = "/conversations";
const roomPath = (webinarId: string) => `${talkPath}/rooms/${encodeURIComponent(webinarId)}`;
export const everyonePath = (webinarId: string) => `${roomPath(webinarId)}/everyone`;
export const placesPath = (webinarId: string) => `${roomPath(webinarId)}/places`;
export const allRoomsPath = `${talkPath}/rooms`;
export const announcementsPath = (webinarId: string) => `${roomPath(webinarId)}/announcements`;
export const filesPath = (webinarId: string) => `${roomPath(webinarId)}/files`;
export const questionsPath = (webinarId: string) => `${roomPath(webinarId)}/questions`;
export const circlePath = (circleId: string) => `${talkPath}/circles/${encodeURIComponent(circleId)}`;
export const directPath = (contactId: string) => `${talkPath}/direct/${encodeURIComponent(contactId)}`;
export const searchPath = (q: string) => `${talkPath}/search?q=${encodeURIComponent(q)}`;
export const directListPath = `${talkPath}?tab=direct`;

export type Place =
  | { kind: "everyone" | "announce" | "files" | "places" | "questions"; webinarId: string }
  | { kind: "circle"; circleId: string }
  | { kind: "direct"; contactId: string }
  | { kind: "search" }
  | { kind: "none" };

/** The place an address is, whatever the workspace prefix. */
export function placeOf(pathname: string): Place {
  const parts = pathname.split("/").filter(Boolean);
  const at = parts.indexOf("conversations");
  const rest = at < 0 ? [] : parts.slice(at + 1).map(decodeURIComponent);
  const [area, id, sub] = rest;
  if (area === "rooms" && id) return { kind: sub === "announcements" ? "announce" : sub === "files" ? "files" : sub === "places" ? "places" : sub === "questions" ? "questions" : "everyone", webinarId: id };
  if (area === "circles" && id) return { kind: "circle", circleId: id };
  if (area === "direct" && id) return { kind: "direct", contactId: id };
  if (area === "search") return { kind: "search" };
  return { kind: "none" };
}

/** The room a place belongs to, so its channels stay open in the rail. */
export function roomOfPlace(place: Place, rail: HostTalkResponse | null): HostRoomRow | null {
  if (!rail) return null;
  if (place.kind === "everyone" || place.kind === "announce" || place.kind === "files" || place.kind === "places" || place.kind === "questions") return rail.rooms.find((r) => r.webinarId === place.webinarId) ?? null;
  if (place.kind === "circle") return rail.rooms.find((r) => r.circles.some((c) => c.circleId === place.circleId)) ?? null;
  return null;
}

/** What a room opens on: Questions for a recording, else its Everyone, or Announcements when Everyone chat is off. */
export function roomHome(room: Pick<HostRoomRow, "webinarId" | "everyoneChat" | "kind">): string {
  if (room.kind === "Plays any time") return questionsPath(room.webinarId);
  return room.everyoneChat ? everyonePath(room.webinarId) : announcementsPath(room.webinarId);
}

/** A search hit's or a file's address; `at` is the line to open on, which the chat scrolls to and lights. */
export function hitPath(place: { kind: "everyone" | "announce" | "circle" | "direct"; id: string }, at?: string): string {
  const path = place.kind === "everyone" ? everyonePath(place.id) : place.kind === "announce" ? announcementsPath(place.id) : place.kind === "circle" ? circlePath(place.id) : directPath(place.id);
  return at && place.kind !== "direct" ? `${path}?at=${encodeURIComponent(at)}` : path;
}
